import React, { useState } from "react";
import {
  FaLeaf,
  FaFire,
  FaTruck,
  FaRecycle,
  FaThermometerHalf,
  FaBoxOpen,
  FaCalendarAlt,
  FaCoffee,
  FaTint,
  FaClock,
} from "react-icons/fa";

const IconEspresso = ({ className = "" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} aria-hidden>
    <path d="M18 28h22c4 0 7 3.2 7 7.2V42H18v-6.8c0-4 3-7.2 7-7.2Z" stroke="currentColor" strokeWidth="2.2" />
    <path d="M47 32h4c3 0 5 2.2 5 5s-2 5-5 5h-4" stroke="currentColor" strokeWidth="2.2" />
    <path d="M22 22c2-4 6-6 10-6s8 2 10 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M16 46h32" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="28" cy="14" r="1.6" fill="currentColor" />
    <circle cx="32" cy="10" r="1.6" fill="currentColor" />
    <circle cx="36" cy="14" r="1.6" fill="currentColor" />
  </svg>
);

const IconPourOver = ({ className = "" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} aria-hidden>
    <path d="M20 20h24l-4 12H24L20 20Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
    <path d="M24 32h16v4c0 6-3.5 10-8 10s-8-4-8-10v-4Z" stroke="currentColor" strokeWidth="2.2" />
    <path d="M28 46h8v4H28z" stroke="currentColor" strokeWidth="2.2" />
    <path d="M32 8v8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M26 12c2 2 4 2 6 0s4-2 6 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const IconFrenchPress = ({ className = "" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} aria-hidden>
    <rect x="20" y="18" width="24" height="30" rx="3" stroke="currentColor" strokeWidth="2.2" />
    <path d="M32 10v8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <circle cx="32" cy="9" r="3" stroke="currentColor" strokeWidth="2.2" />
    <path d="M24 28h16M24 34h16M24 40h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" opacity="0.7" />
    <path d="M44 24c4 0 6 2.5 6 6s-2 6-6 6" stroke="currentColor" strokeWidth="2.2" />
  </svg>
);

const IconMoka = ({ className = "" }) => (
  <svg viewBox="0 0 64 64" fill="none" className={className} aria-hidden>
    <path d="M22 34h20l-2 16H24L22 34Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
    <path d="M24 34c0-6 3.5-10 8-10s8 4 8 10" stroke="currentColor" strokeWidth="2.2" />
    <path d="M28 18h8v6h-8z" stroke="currentColor" strokeWidth="2.2" />
    <path d="M30 12h4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    <path d="M44 38c3 0 5 2 5 4.5S47 47 44 47" stroke="currentColor" strokeWidth="2.2" />
    <path d="M28 8c1.5 2 3 2 4 0s3-2 4 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const methods = [
  {
    id: "espresso",
    name: "Espresso",
    tagline: "Rich & Intense",
    coffee: "18g",
    water: "36g yield",
    time: "25–30 sec",
    grind: "Fine",
    difficulty: 5,
    Icon: IconEspresso,
  },
  {
    id: "pourover",
    name: "Pour Over",
    tagline: "Clean & Bright",
    coffee: "20g",
    water: "300ml",
    time: "2:30 min",
    grind: "Medium",
    difficulty: 4,
    Icon: IconPourOver,
  },
  {
    id: "french",
    name: "French Press",
    tagline: "Bold & Full",
    coffee: "30g",
    water: "500ml",
    time: "4:00 min",
    grind: "Coarse",
    difficulty: 2,
    Icon: IconFrenchPress,
  },
  {
    id: "moka",
    name: "Moka Pot",
    tagline: "Balanced & Classic",
    coffee: "20g",
    water: "100ml",
    time: "4:00 min",
    grind: "Medium-Fine",
    difficulty: 3,
    Icon: IconMoka,
  },
];

const roasts = [
  { id: "light", label: "Light" },
  { id: "medium", label: "Medium" },
  { id: "dark", label: "Dark" },
];

const facts = [
  { icon: FaFire, title: "Freshly Roasted", text: "Small batches roasted every week." },
  { icon: FaLeaf, title: "Single Origin", text: "One district per bag—Rulindo, Kamonyi, or Karongi." },
  { icon: FaTruck, title: "Fast Delivery", text: "Ships within 24 hours across Rwanda." },
  { icon: FaRecycle, title: "Sustainable", text: "Recyclable packaging and ethical sourcing." },
];

const Difficulty = ({ level }) => (
  <div className="mt-4">
    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-cocoa/70">
      Difficulty
    </p>
    <div className="flex gap-1.5">
      {[1, 2, 3, 4, 5, 6].map((n) => (
        <span
          key={n}
          className={`h-2.5 w-2.5 rounded-full ${
            n <= level ? "bg-gold" : "border border-cream-muted bg-transparent"
          }`}
        />
      ))}
    </div>
  </div>
);

const BrewGuide = () => {
  const [roast, setRoast] = useState("medium");

  return (
    <section className="overflow-hidden bg-cream px-4 py-16 sm:px-6 md:px-8 md:py-24 lg:px-12">
      <div className="relative mx-auto max-w-7xl">
        <FaCoffee
          className="pointer-events-none absolute -left-1 top-8 hidden text-4xl text-gold/20 md:block"
          aria-hidden
        />
        <FaLeaf
          className="pointer-events-none absolute right-4 top-16 hidden text-5xl text-gold/15 lg:block"
          aria-hidden
        />

        <div className="mb-12 flex flex-col gap-10 lg:mb-16 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-xl">
            <div className="mb-3 flex items-center gap-2">
              <FaLeaf className="text-gold" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gold">
                Brewing Guide
              </p>
            </div>
            <h2 className="font-display text-3xl text-espresso sm:text-4xl md:text-5xl">
              Brew It Your Way
            </h2>
            <p className="mt-4 text-base leading-relaxed text-cocoa md:text-lg">
              Start with <strong>10g per 180ml</strong> of water, then adjust to taste.
              Best within 6–12 weeks of roast.
            </p>
          </div>

          <div className="w-full max-w-sm rounded-2xl border border-cream-muted bg-white p-5 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold">
              Choose Your Roast
            </p>
            <div className="relative mt-5 flex items-center justify-between px-1">
              <div className="absolute left-3 right-3 top-1/2 h-px -translate-y-1/2 bg-cream-muted" />
              {roasts.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setRoast(item.id)}
                  className="relative z-10 flex flex-col items-center gap-2"
                >
                  <span
                    className={`h-3.5 w-3.5 rounded-full transition ${
                      roast === item.id
                        ? "bg-gold ring-4 ring-gold/25"
                        : "border border-cream-muted bg-white"
                    }`}
                  />
                  <span
                    className={`text-xs font-semibold uppercase tracking-wide ${
                      roast === item.id ? "text-espresso" : "text-cocoa/70"
                    }`}
                  >
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
            <p className="mt-4 text-sm text-cocoa">
              {roast === "light" && "Bright acidity and delicate aromatics."}
              {roast === "medium" && "Balanced flavor and aroma for every brew."}
              {roast === "dark" && "Deep body with chocolate and caramel notes."}
            </p>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[1.55fr_0.85fr] xl:gap-8">
          <div className="grid gap-5 sm:grid-cols-2">
            {methods.map(({ Icon, ...method }) => (
              <article
                key={method.id}
                className="group rounded-2xl border border-cream-muted bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1.5 hover:border-gold hover:shadow-xl sm:p-6"
              >
                <div className="mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-cream-warm text-espresso transition group-hover:bg-espresso group-hover:text-gold">
                  <Icon className="h-12 w-12" />
                </div>

                <h3 className="font-display text-2xl text-espresso">{method.name}</h3>
                <p className="mt-1 text-sm font-medium text-gold">{method.tagline}</p>

                <div className="mt-5 grid grid-cols-3 gap-2 rounded-xl bg-cream-warm/80 p-3 text-center">
                  <div>
                    <FaCoffee className="mx-auto text-gold" />
                    <p className="mt-1.5 text-xs font-semibold text-espresso">{method.coffee}</p>
                    <p className="text-[10px] uppercase tracking-wide text-cocoa">Coffee</p>
                  </div>
                  <div>
                    <FaTint className="mx-auto text-gold" />
                    <p className="mt-1.5 text-xs font-semibold text-espresso">{method.water}</p>
                    <p className="text-[10px] uppercase tracking-wide text-cocoa">Water</p>
                  </div>
                  <div>
                    <FaClock className="mx-auto text-gold" />
                    <p className="mt-1.5 text-xs font-semibold text-espresso">{method.time}</p>
                    <p className="text-[10px] uppercase tracking-wide text-cocoa">Time</p>
                  </div>
                </div>

                <p className="mt-4 text-sm text-cocoa">
                  Grind: <strong className="text-espresso">{method.grind}</strong>
                </p>
                <Difficulty level={method.difficulty} />
              </article>
            ))}
          </div>

          <aside className="relative min-h-[28rem] overflow-hidden rounded-2xl sm:min-h-[32rem] xl:min-h-full">
            <img
              src="/hero-slide.png"
              alt="KBL Coffee bag with steaming cup"
              className="absolute inset-0 h-full w-full object-cover object-[70%_center]"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-espresso/88 via-espresso/55 to-espresso/25" />
            <div className="relative z-10 flex h-full flex-col justify-end p-6 text-cream sm:p-8">
              <h3 className="font-display text-3xl md:text-4xl">Freshness Matters</h3>
              <ul className="mt-6 space-y-4 text-sm md:text-base">
                <li className="flex items-start gap-3">
                  <FaThermometerHalf className="mt-1 shrink-0 text-gold" />
                  <span>Store in a cool, dry place</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaBoxOpen className="mt-1 shrink-0 text-gold" />
                  <span>Reseal after every brew</span>
                </li>
                <li className="flex items-start gap-3">
                  <FaCalendarAlt className="mt-1 shrink-0 text-gold" />
                  <span>Best within 6–12 weeks after roast</span>
                </li>
              </ul>
            </div>
          </aside>
        </div>

        <div className="mt-12 grid gap-4 rounded-2xl bg-espresso p-5 text-cream sm:grid-cols-2 sm:p-6 lg:mt-16 lg:grid-cols-4">
          {facts.map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gold/15 text-gold">
                <Icon />
              </span>
              <div>
                <h4 className="text-sm font-semibold">{title}</h4>
                <p className="mt-1 text-xs leading-relaxed text-cream/70">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrewGuide;
